import { useState } from 'react';

// ─────────────────────────────────────────────────────────────
// Éditeur multilingue réutilisable (spec 27)
// Permet de saisir l'intitulé et la description d'une entité
// (produit, service…) dans plusieurs langues, sans dupliquer
// l'entité. Le résultat est stocké dans un champ `translation`.
// ─────────────────────────────────────────────────────────────

export interface TranslationLanguage {
  code: string;
  label: string;
  flag: string | null;
  isDefault?: boolean;
}

export type EntityTranslation = Record<string, { name?: string; description?: string }>;

interface Props {
  languages: TranslationLanguage[];
  value: EntityTranslation;
  onChange: (value: EntityTranslation) => void;
  label?: string;
  hint?: string;
}

const INPUT =
  'w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors';

export default function TranslationEditor({ languages, value, onChange, label = 'Traductions', hint }: Props) {
  const [active, setActive] = useState<string>(languages[0]?.code || '');
  const target = languages.find((l) => l.code === active) || languages[0];

  const update = (code: string, field: 'name' | 'description', val: string) => {
    const next: EntityTranslation = { ...value };
    next[code] = { ...(next[code] || {}), [field]: val };
    onChange(next);
  };

  if (languages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-background-300/60 bg-background-50 p-4 flex items-start gap-3">
        <i className="ri-translate-2 text-lg text-foreground-400 mt-0.5"></i>
        <div>
          <p className="text-sm font-medium text-foreground-700">{label}</p>
          <p className="text-xs text-foreground-500 mt-0.5">
            Aucune langue active. Activez des langues dans <span className="font-semibold">Socle central → Langues</span> pour proposer des versions traduites.
          </p>
        </div>
      </div>
    );
  }

  const filled = Object.entries(value).filter(([, v]) => (v?.name || '').trim() || (v?.description || '').trim()).length;

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-translate-2 text-secondary-600"></i>
          <span className="text-sm font-semibold text-foreground-800">{label}</span>
          {filled > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">
              {filled} langue{filled > 1 ? 's' : ''} remplie{filled > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <span className="text-[11px] text-foreground-400 whitespace-nowrap">
          {languageNote(languages)}
        </span>
      </div>

      {hint && <p className="text-xs text-foreground-500 mb-3">{hint}</p>}

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 mb-4 w-fit max-w-full overflow-x-auto">
        {languages.map((l) => {
          const t = value[l.code];
          const isFilled = !!((t?.name || '').trim() || (t?.description || '').trim());
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setActive(l.code)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                active === l.code ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <span>{l.flag || '🌐'}</span>
              {l.label}
              {isFilled && <i className="ri-checkbox-circle-fill text-accent-500 text-[11px]"></i>}
            </button>
          );
        })}
      </div>

      {target && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">
              Intitulé ({target.label})
            </span>
            <input
              type="text"
              value={value[target.code]?.name || ''}
              onChange={(e) => update(target.code, 'name', e.target.value)}
              placeholder="Ex : Consultation"
              className={INPUT}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">
              Description ({target.label})
            </span>
            <textarea
              rows={3}
              value={value[target.code]?.description || ''}
              onChange={(e) => update(target.code, 'description', e.target.value)}
              placeholder="Décrivez le produit/service dans cette langue…"
              className={`${INPUT} resize-none`}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function languageNote(languages: TranslationLanguage[]): string {
  const def = languages.find((l) => l.isDefault);
  return def ? `La langue principale (${def.label}) reste dans les champs ci-dessus` : 'Une version par langue';
}